// main.js — bootstrap da v19.
//
// Responsabilidades:
//  1. Carregar estilos globais.
//  2. Executar migrações de storage (chaves mbo_* → eloq:*).
//  3. Inicializar Google Analytics 4 + Consent Mode v2 (Fase B).
//  4. Registrar rotas e conectar cada rota ao módulo renderizador.
//  5. Splash inicial: redireciona para /login se não autenticado,
//     para /aluno (ou /admin) se já tem sessão.
//  6. Expor helpers no console em dev.

import './styles/index.css';
import { api }     from './core/api.js';
import { auth }    from './core/auth.js';
import { notify }  from './core/notify.js';
import { router }  from './core/router.js';
import { storage } from './core/storage.js';
import { runMigrations } from './core/migrations.js';
import { renderRouteSkeleton, renderRouteError } from './core/route-fallback.js';
import { registerPWA } from './core/pwa.js';
import { initAnalytics, trackPageView } from './core/analytics.js';
import { initLgpdFooter } from './aluno/lgpd-footer.js';
import {
  renderTermoPage,
  renderPrivacidadePage,
  renderConsentimentosPage,
} from './aluno/lgpd-pages.js';

// Módulos do aluno (Fase 5)
import * as login      from './aluno/login.js';
import * as home       from './aluno/home.js';
import * as frequencia from './aluno/frequencia.js';
import * as oratoria   from './aluno/oratoria.js';
import * as financeiro from './aluno/financeiro.js';
import * as materiais  from './aluno/materiais.js';

// Módulos do admin (Fase 6)
// Os módulos "core" do admin (dashboard, frequencia-grid, lancar, conteudo)
// entram no bundle principal porque são acessados logo após o login admin.
// Já `importar` e `financeiro-admin` são **lazy-loaded** (Fase 9) — viram
// chunks separados e só baixam quando o admin entra nessas rotas específicas.
import * as dashboard      from './admin/dashboard.js';
import * as frequenciaGrid from './admin/frequencia-grid.js';
import * as lancar         from './admin/lancar.js';
import * as conteudo       from './admin/conteudo.js';

console.log('[Eloquence] boot v19.2-analytics-events');

// ── Google Analytics 4 + Consent Mode v2 (Fases B + C) ─────────────────────
// B: defaults "denied", carga de gtag.js, applyConsent baseado em sessionStorage.
// C: eventos customizados (login, page_view SPA, lgpd_accept_all, lgpd_revoke).
// initAnalytics() injeta defaults; page_view é disparado pelo router.onNavigate.
initAnalytics();

// ── Migrações de storage ──────────────────────────────────────────────────────
try {
  const report = runMigrations();
  if (report.migrated.length || report.dropped.length) {
    console.log('[migrations]', {
      migradas: report.migrated,
      puladas:  report.skipped,
      obsoletas_presentes: report.dropped,
    });
  }
} catch (e) {
  console.error('[migrations] falha, seguindo boot mesmo assim:', e);
}

const app = document.getElementById('app');

// ── Guards de autenticação ────────────────────────────────────────────────────

/**
 * Exige usuário logado para renderizar uma rota.
 * Se não estiver logado, redireciona para /login.
 */
function requireAuth(renderFn) {
  return (ctx) => {
    if (!auth.isLoggedIn()) {
      router.go('/login');
      return;
    }
    renderFn(app, ctx);
  };
}

/**
 * Exige usuário logado E admin.
 */
function requireAdmin(renderFn) {
  return (ctx) => {
    if (!auth.isLoggedIn() || !auth.isAdmin()) {
      router.go('/login');
      return;
    }
    renderFn(app, ctx);
  };
}

// ── Registro de rotas ─────────────────────────────────────────────────────────

// Splash: decide destino inicial com base na sessão
router.register('/', () => {
  if (auth.isLoggedIn()) {
    router.go(auth.isAdmin() ? '/admin' : '/aluno');
  } else {
    router.go('/login');
  }
});

// Rotas públicas (não exigem auth)
router.register('/login', () => login.render(app));

// Rotas LGPD públicas — qualquer pessoa pode ler o termo e a política.
router.register('/termo',          () => renderTermoPage(app));
router.register('/privacidade',    () => renderPrivacidadePage(app));
router.register('/consentimentos', () => renderConsentimentosPage(app));

// Rotas do aluno (exigem auth)
router.register('/aluno',             requireAuth(home.render));
router.register('/aluno/frequencia',  requireAuth(frequencia.render));
router.register('/aluno/oratoria',    requireAuth(oratoria.render));
router.register('/aluno/financeiro',  requireAuth(financeiro.render));
router.register('/aluno/materiais',   requireAuth(materiais.render));

// Rotas do admin (Fase 6)
router.register('/admin',             requireAdmin(dashboard.render));
router.register('/admin/frequencia',  requireAdmin(frequenciaGrid.render));
router.register('/admin/lancar',      requireAdmin(lancar.render));
router.register('/admin/conteudo',    requireAdmin(conteudo.render));

// Rotas admin lazy-loaded (Fase 9).
// Mostra skeleton enquanto o chunk é baixado, depois renderiza normalmente.
router.register('/admin/importar',    requireAdmin((container, ctx) => {
  renderRouteSkeleton(container, 'Importar Planilhas');
  import('./admin/importar.js')
    .then(mod => { if (container.isConnected) mod.render(container, ctx); })
    .catch(err => renderRouteError(container, 'Importar Planilhas', err));
}));
router.register('/admin/financeiro',  requireAdmin((container, ctx) => {
  renderRouteSkeleton(container, 'Financeiro');
  import('./admin/financeiro-admin.js')
    .then(mod => { if (container.isConnected) mod.render(container, ctx); })
    .catch(err => renderRouteError(container, 'Financeiro', err));
}));

// Rota /admin/historico — Fase 8 (LOG_AUDITORIA).
// Lazy-loaded para não pesar no bundle principal; tem filtros + lista.
router.register('/admin/historico', requireAdmin((container, ctx) => {
  renderRouteSkeleton(container, 'Histórico');
  import('./admin/historico.js')
    .then(mod => { if (container.isConnected) mod.render(container, ctx); })
    .catch(err => renderRouteError(container, 'Histórico', err));
}));

// Fallback 404
router.register('*', (ctx) => {
  app.innerHTML = `
    <div style="padding:32px;font-family:Montserrat,sans-serif;text-align:center;">
      <h1 style="color:#6A001F">404</h1>
      <p style="color:#666">Rota <code>${ctx.path}</code> não existe.</p>
      <p><a href="#/">voltar para o início</a></p>
    </div>
  `;
});

router.onNavigate((from, to) => {
  console.log(`[router] ${from ?? '—'} → ${to}`);
  // Tira a classe `splash-active` (que tranca o scroll do body com
  // overflow:hidden) assim que a primeira rota for renderizada.
  // Sem isso, todas as telas internas ficam sem scroll vertical.
  if (document.body.classList.contains('splash-active')) {
    document.body.classList.remove('splash-active');
  }
  // Analytics (Fase C): page_view manual para SPA com hash routing.
  // Enviamos apenas o path — GA4 normaliza internamente. Nenhum dado pessoal.
  trackPageView(to);
});

// Re-dispatch após login/logout para guards pegarem a mudança
auth.onChange(() => {
  const current = router.getCurrent();
  router.go(current.path, current.params);
});

router.start('/');

// Rodapé LGPD fixo em todas as rotas autenticadas (Ponto 3 — H2).
// Idempotente; injeta um único nodo no <body> e gerencia visibilidade via hashchange.
initLgpdFooter();

// Registra service worker em produção (Fase 11).
registerPWA();

// ── Dev helpers ───────────────────────────────────────────────────────────────
if (import.meta.env.DEV) {
  window.__eloq = { api, auth, notify, router, storage, runMigrations };
  console.log('[Eloquence] dev helpers em window.__eloq');
}
